import type { BillStatus, CustomerDTO, PaymentMethod } from '@six-pockets/shared';
import type { Parts } from './split';

export type BillStep = 'items' | 'payment';

/** One bill of the modal as staff filled it in (the modal holds several after Split). */
export interface BillSheet {
  /** A split bill's own name (the team, e.g. "Arun & Yashu"); '' for none. */
  name: string;
  saleIds: string[];
  customer: CustomerDTO | null;
  discount: string;
  status: BillStatus;
  method: PaymentMethod;
  upi: string;
  cash: string;
  paid: string;
}

/** What staff had filled in on a bill when its modal was closed without generating it. */
export interface BillDraft {
  step: BillStep;
  /** The bill shown (an index into sheets). */
  active: number;
  sheets: BillSheet[];
  /** Each booking's amount on each sheet (only when split). */
  parts: Parts | null;
}

/** What a bill was opened with: the ticked bookings, or (a condiments-only bill) the tab it was started from. */
export interface BillOpenedWith {
  bookingIds: string[];
  saleIds: string[];
}

/**
 * Unfinished bills, kept while the app stays open. Only what was filled in on the modal: the bookings stay ticked on
 * the cards by themselves. `openedWith` is what the bill had when it was first opened, and stays that.
 *
 * The rule (owner's call, 2026-10-12): Generate Bill resumes a closed bill while any of the bookings it was first
 * opened with is still ticked, so ticking another booking, or unticking one, keeps it. Unticking everything ends it
 * (`clearBookingBillDrafts`): whatever is ticked after that is a new bill, even the same bookings.
 */
let drafts: { openedWith: BillOpenedWith; draft: BillDraft }[] = [];

const shared = (a: string[], b: string[]) => a.filter((id) => b.includes(id)).length;

/** The saved bill this one carries on from: a bookings bill by its bookings, a condiments-only bill by its tab. */
function find(now: BillOpenedWith) {
  const matches = now.bookingIds.length
    ? drafts.filter((d) => shared(d.openedWith.bookingIds, now.bookingIds) > 0)
    : drafts.filter((d) => !d.openedWith.bookingIds.length && shared(d.openedWith.saleIds, now.saleIds) > 0);
  // With several, the one with the most of its bookings still ticked.
  return matches.sort((a, b) => shared(b.openedWith.bookingIds, now.bookingIds) - shared(a.openedWith.bookingIds, now.bookingIds))[0];
}

export function readBillDraft(openedWith: BillOpenedWith): BillDraft | undefined {
  return find(openedWith)?.draft;
}

export function saveBillDraft(openedWith: BillOpenedWith, draft: BillDraft): void {
  const saved = find(openedWith);
  if (saved) saved.draft = draft;
  else drafts.push({ openedWith, draft });
}

/** The bill was generated: its draft goes, with any other draft that had one of its bookings or tabs. */
export function clearBillDraft(billed: BillOpenedWith): void {
  drafts = drafts.filter((d) => !shared(d.openedWith.bookingIds, billed.bookingIds) && !shared(d.openedWith.saleIds, billed.saleIds));
}

/** Every booking was unticked: the bills they were on are over. */
export function clearBookingBillDrafts(): void {
  drafts = drafts.filter((d) => !d.openedWith.bookingIds.length);
}
